import { useEffect, useState } from 'react';
import AdminLayout from '@/components/feature/AdminLayout';
import { supabase } from '@/lib/supabase';

const pad = (n: number) => String(n).padStart(2, '0');

const toLocalInput = (iso: string | null) => {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
};

const toIso = (localInput: string) => {
  const d = new Date(localInput);
  if (Number.isNaN(d.getTime())) return null;
  return d.toISOString();
};

export default function AdminMaintenance() {
  const [enabled, setEnabled] = useState(false);
  const [startAt, setStartAt] = useState('');
  const [endAt, setEndAt] = useState('');
  const [title, setTitle] = useState('');
  const [message, setMessage] = useState('');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [feedback, setFeedback] = useState<{ type: 'success' | 'error'; text: string } | null>(null);

  const load = async () => {
    setLoading(true);
    setFeedback(null);
    try {
      const { data, error } = await supabase
        .from('site_settings')
        .select('key, value')
        .eq('group', 'maintenance');

      if (data && data.length > 0) {
        const map: Record<string, string> = {};
        data.forEach((r) => {
          map[r.key] = r.value ?? '';
        });

        setEnabled(map.maintenance_enabled === 'true');
        setStartAt(toLocalInput(map.maintenance_start || null));
        setEndAt(toLocalInput(map.maintenance_end || null));
        setTitle(map.maintenance_title || '');
        setMessage(map.maintenance_message || '');
      }
      // Nếu không có dữ liệu thì giữ giá trị mặc định, không báo lỗi
      if (error && (!data || data.length === 0)) throw error;
    } catch (err) {
      setFeedback({
        type: 'error',
        text: err instanceof Error ? err.message : 'Không thể tải cài đặt.',
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const save = async () => {
    setSaving(true);
    setFeedback(null);

    if (startAt && endAt && new Date(startAt) >= new Date(endAt)) {
      setFeedback({ type: 'error', text: 'Thời gian kết thúc phải sau thời gian bắt đầu.' });
      setSaving(false);
      return;
    }

    try {
      const rows = [
        { key: 'maintenance_enabled', value: String(enabled), group: 'maintenance' },
        { key: 'maintenance_start', value: startAt ? toIso(startAt) : null, group: 'maintenance' },
        { key: 'maintenance_end', value: endAt ? toIso(endAt) : null, group: 'maintenance' },
        { key: 'maintenance_title', value: title, group: 'maintenance' },
        { key: 'maintenance_message', value: message, group: 'maintenance' },
      ];

      const { error } = await supabase.from('site_settings').upsert(rows, { onConflict: 'key' });
      if (error) throw error;

      setFeedback({ type: 'success', text: 'Đã lưu cài đặt bảo trì.' });
    } catch (err) {
      setFeedback({
        type: 'error',
        text: err instanceof Error ? err.message : 'Không thể lưu cài đặt.',
      });
    } finally {
      setSaving(false);
    }
  };

  return (
    <AdminLayout>
      <div className="max-w-3xl mx-auto">
        <div className="mb-6">
          <h1 className="text-2xl font-bold text-foreground-950">Chế độ bảo trì</h1>
          <p className="text-foreground-600 text-sm mt-1">
            Tự động đóng toàn bộ trang web trong khung thời gian bạn thiết lập.
          </p>
        </div>

        {feedback && (
          <div
            className={`mb-4 rounded-lg p-3 flex items-center gap-2 border ${
              feedback.type === 'success'
                ? 'bg-emerald-500/15 border-emerald-500/30'
                : 'bg-rose-500/15 border-rose-500/30'
            }`}
          >
            <i
              className={`${
                feedback.type === 'success' ? 'ri-checkbox-circle-line text-emerald-400' : 'ri-error-warning-line text-rose-400'
              } text-lg`}
            ></i>
            <p className={`text-sm ${feedback.type === 'success' ? 'text-emerald-400' : 'text-rose-400'}`}>{feedback.text}</p>
          </div>
        )}

        {loading ? (
          <div className="py-20 text-center text-sm text-foreground-600">Đang tải...</div>
        ) : (
          <div className="rounded-xl border border-background-200/60 bg-background-100/50 p-6 space-y-6">
            <div className="flex items-center justify-between gap-4">
              <div>
                <h2 className="text-base font-semibold text-foreground-950">Bật chế độ bảo trì</h2>
                <p className="text-sm text-foreground-600 mt-0.5">
                  {enabled ? 'Trang web đang ở trạng thái bảo trì.' : 'Trang web đang hoạt động bình thường.'}
                </p>
              </div>
              <button
                onClick={() => setEnabled((v) => !v)}
                className={`relative w-14 h-8 rounded-full transition-colors cursor-pointer shrink-0 ${
                  enabled ? 'bg-accent-500' : 'bg-background-300'
                }`}
                aria-pressed={enabled}
              >
                <span
                  className={`absolute top-1 w-6 h-6 rounded-full bg-background-50 transition-all ${
                    enabled ? 'left-7' : 'left-1'
                  }`}
                ></span>
              </button>
            </div>

            <div className="border-t border-background-200/60 pt-6">
              <h3 className="text-sm font-semibold text-foreground-900 mb-4">Khung thời gian</h3>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs font-medium text-foreground-600 mb-1.5">Bắt đầu bảo trì</label>
                  <input
                    type="datetime-local"
                    value={startAt}
                    onChange={(e) => setStartAt(e.target.value)}
                    className="w-full px-3 py-2.5 rounded-md bg-background-50 border border-background-300/60 text-sm text-foreground-900 focus:outline-none focus:border-accent-500"
                  />
                  <p className="text-[11px] text-foreground-500 mt-1">Để trống để bảo trì ngay lập tức.</p>
                </div>
                <div>
                  <label className="block text-xs font-medium text-foreground-600 mb-1.5">Kết thúc bảo trì</label>
                  <input
                    type="datetime-local"
                    value={endAt}
                    onChange={(e) => setEndAt(e.target.value)}
                    className="w-full px-3 py-2.5 rounded-md bg-background-50 border border-background-300/60 text-sm text-foreground-900 focus:outline-none focus:border-accent-500"
                  />
                  <p className="text-[11px] text-foreground-500 mt-1">Để trống để bảo trì không giới hạn.</p>
                </div>
              </div>
            </div>

            <div className="border-t border-background-200/60 pt-6 space-y-4">
              <h3 className="text-sm font-semibold text-foreground-900">Nội dung hiển thị</h3>
              <div>
                <label className="block text-xs font-medium text-foreground-600 mb-1.5">Tiêu đề</label>
                <input
                  type="text"
                  value={title}
                  onChange={(e) => setTitle(e.target.value)}
                  placeholder="Website đang bảo trì"
                  className="w-full px-3 py-2.5 rounded-md bg-background-50 border border-background-300/60 text-sm text-foreground-900 focus:outline-none focus:border-accent-500"
                />
              </div>
              <div>
                <label className="block text-xs font-medium text-foreground-600 mb-1.5">Thông báo</label>
                <textarea
                  value={message}
                  onChange={(e) => setMessage(e.target.value)}
                  rows={3}
                  maxLength={500}
                  placeholder="Chúng tôi đang nâng cấp hệ thống..."
                  className="w-full px-3 py-2.5 rounded-md bg-background-50 border border-background-300/60 text-sm text-foreground-900 focus:outline-none focus:border-accent-500 resize-none"
                ></textarea>
                <p className="text-[11px] text-foreground-500 mt-1 text-right">{message.length}/500</p>
              </div>
            </div>

            <div className="border-t border-background-200/60 pt-6 flex justify-end">
              <button
                onClick={save}
                disabled={saving}
                className="px-6 py-2.5 rounded-md bg-accent-500 text-background-950 font-semibold text-sm hover:bg-accent-600 disabled:opacity-60 cursor-pointer whitespace-nowrap transition-colors"
              >
                {saving ? 'Đang lưu...' : 'Lưu cài đặt'}
              </button>
            </div>
          </div>
        )}
      </div>
    </AdminLayout>
  );
}